/**
 * The rules that decide whether a trailer somebody proposes is taken.
 *
 * Deliberately pure: no network, no clock, no storage. The server calls this
 * on every community submission and `scripts/trailers.mjs` calls it on the
 * maintainer path, so both apply exactly the same policy. Anything that needs
 * to talk to YouTube lives in the server's oEmbed client instead.
 *
 * There is no moderation queue by design. A submission either clears every
 * gate here and goes live immediately, or it is rejected and never stored —
 * so nobody has to look at a backlog for the catalog to stay clean.
 */
/**
 * Channels trusted as the official source for a studio/distributor trailer.
 * Not exhaustive — new distributors show up over time — but a trailer from
 * outside this list is never accepted from the community: a commercial
 * operator like YouTube is presumed to know whether what it embeds was
 * uploaded lawfully, and we only take its word for that on channels that are
 * recognizably the studio itself.
 */
export const OFFICIAL_CHANNELS = [
    'Warner Bros. Pictures',
    'Warner Bros. Entertainment',
    'Warner Bros. UK & Ireland',
    'Warner Bros. Germany',
    'Warner Bros. Pictures España',
    'Warner Bros. Pictures Latinoamérica',
    'Warner Bros. France',
    'Warner Bros. Pictures Brasil',
    'Warner Bros. Canada',
    'Warner Bros. Italia',
    'Sony Pictures Entertainment',
    'Universal Pictures',
    'Paramount Pictures',
    '20th Century Studios',
    'Walt Disney Studios',
    'Marvel Entertainment',
    'Star Wars',
    'Pixar',
    'A24',
    'Netflix',
    'Prime Video',
    'Apple TV',
    'HBO Max',
    'Focus Features',
    'Lionsgate Movies',
    'Searchlight Pictures',
    'MGM',
    'Constantin Film',
    'StudioCanal',
    'Rotten Tomatoes Trailers',
    'Movieclips Trailers',
    'DreamWorks Animation',
    'Studio Ghibli',
    'GKIDS Films',
    'Crunchyroll',
    'Sony Pictures Classics',
    'Criterion',
    'NEON',
    'Universal Pictures Germany',
    'Paramount Pictures Germany',
    'Disney Deutschland',
    '20th Century Studios Deutschland',
    'STUDIOCANAL Germany',
    'X Verleih AG',
];
const officialChannelSet = new Set(OFFICIAL_CHANNELS.map((name) => name.toLowerCase()));
export function isOfficialChannel(channel) {
    return channel !== undefined && officialChannelSet.has(channel.trim().toLowerCase());
}
/** A YouTube id: 11 characters of the URL-safe base64-ish alphabet. */
export const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;
/**
 * `https://<host>/<path>?<query>` for the hosts we accept, and nothing else.
 * The scheme is required, so `javascript:` and protocol-relative links never
 * get as far as the host check.
 */
const URL_PATTERN = /^https?:\/\/(?:www\.|m\.)?(youtube\.com|youtube-nocookie\.com|youtu\.be)(\/[^?#]*)?(\?[^#]*)?(?:#.*)?$/i;
/**
 * Accepts a bare 11-character video id or a common YouTube URL form
 * (watch?v=, youtu.be/, /embed/, /shorts/, with or without extra query
 * params) and returns just the id.
 *
 * Anything else is refused outright — no other host, and no arbitrary URL. The
 * whole safety argument rests on YouTube vetting what it serves, and that
 * argument does not carry over to a link somebody pastes.
 */
export function extractVideoId(input) {
    const trimmed = input.trim();
    if (YOUTUBE_ID_PATTERN.test(trimmed))
        return { ok: true, videoId: trimmed };
    // Parsed by hand rather than with `URL`: this module is compiled against the
    // bare ES library so it stays usable everywhere, and the shapes we accept
    // are narrow enough to spell out.
    const parts = URL_PATTERN.exec(trimmed);
    if (!parts)
        return { ok: false, reason: 'not_youtube' };
    const [, host = '', path = '', query = ''] = parts;
    let candidate;
    if (host === 'youtu.be') {
        candidate = path.slice(1);
    }
    else if (path === '/watch') {
        candidate = /[?&]v=([^&]*)/.exec(query)?.[1];
    }
    else if (path.startsWith('/embed/')) {
        candidate = path.slice('/embed/'.length);
    }
    else if (path.startsWith('/shorts/')) {
        candidate = path.slice('/shorts/'.length);
    }
    // Guard against a trailing path segment (e.g. /embed/ID/extra).
    candidate = candidate ? candidate.split('/')[0] : candidate;
    if (candidate && YOUTUBE_ID_PATTERN.test(candidate))
        return { ok: true, videoId: candidate };
    return { ok: false, reason: 'not_youtube' };
}
/**
 * Folds a title down to comparable words: lower case, accents removed, `&`
 * spelled out, everything else that is not a letter or digit turned into a
 * separator. "WALL·E" and "Wall-E" end up the same, "Amélie" matches "Amelie".
 */
export function normalizeTitle(value) {
    return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .split(' ')
        .filter(Boolean);
}
/** Index of `needle` as a run of consecutive words in `haystack`, or -1. */
function indexOfWords(haystack, needle) {
    if (needle.length === 0 || needle.length > haystack.length)
        return -1;
    outer: for (let start = 0; start <= haystack.length - needle.length; start += 1) {
        for (let offset = 0; offset < needle.length; offset += 1) {
            if (haystack[start + offset] !== needle[offset])
                continue outer;
        }
        return start;
    }
    return -1;
}
/** Words that mark a video as a trailer rather than a clip, review or edit. */
const TRAILER_WORDS = new Set(['trailer', 'teaser', 'trailers']);
/**
 * Does this video's own title plausibly describe a trailer for this movie?
 *
 * The channel whitelist alone cannot catch the common mistake of a genuinely
 * official channel hosting a video for a *different* film, so the title has to
 * corroborate it: the movie's name must appear as whole words, the video must
 * call itself a trailer or teaser, and any release year it mentions must be
 * the movie's own.
 */
export function matchesMovieTitle(movie, videoTitle) {
    const videoWords = normalizeTitle(videoTitle);
    if (!videoWords.some((word) => TRAILER_WORDS.has(word))) {
        return { ok: false, reason: 'not_a_trailer' };
    }
    const candidates = [movie.title, movie.originalTitle]
        .filter((value) => Boolean(value))
        .map(normalizeTitle)
        .filter((words) => words.length > 0);
    let matchStart = -1;
    let matchLength = 0;
    for (const words of candidates) {
        const at = indexOfWords(videoWords, words);
        if (at !== -1) {
            matchStart = at;
            matchLength = words.length;
            break;
        }
    }
    if (matchStart === -1)
        return { ok: false, reason: 'title_mismatch' };
    // Years inside the matched title itself ("Blade Runner 2049") say nothing
    // about the release date, so look only at the words around it.
    const rest = [...videoWords.slice(0, matchStart), ...videoWords.slice(matchStart + matchLength)];
    const years = rest
        .filter((word) => /^(18|19|20)\d{2}$/.test(word))
        .map(Number)
        .filter((year) => year >= 1888 && year <= 2100);
    // A trailer usually goes up in the release year or the one before it. Only
    // reject when the video names a year, the movie's own year is known, and
    // none of the video's years fit.
    if (movie.year !== null &&
        years.length > 0 &&
        !years.some((year) => Math.abs(year - movie.year) <= 1)) {
        return { ok: false, reason: 'year_mismatch' };
    }
    return { ok: true };
}
